import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const base = (size?: number, className?: string) => ({
  width: size ?? 24,
  height: size ?? 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className,
});

/* Animals */
export const CowIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M5 9c0-3 2-5 7-5s7 2 7 5v3c0 3-2 5-5 5h-4c-3 0-5-2-5-5V9z" />
    <path d="M5 9 3 7M19 9l2-2" />
    <circle cx="9" cy="11" r=".8" fill="currentColor" />
    <circle cx="15" cy="11" r=".8" fill="currentColor" />
    <path d="M10 15c.5.6 1.2 1 2 1s1.5-.4 2-1" />
  </svg>
);

export const PigIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M4 12c0-3.5 3-6 8-6s8 2.5 8 6c0 4-3 6-8 6s-8-2-8-6z" />
    <ellipse cx="12" cy="13" rx="2.5" ry="1.6" />
    <circle cx="11" cy="13" r=".4" fill="currentColor" />
    <circle cx="13" cy="13" r=".4" fill="currentColor" />
    <circle cx="9" cy="10" r=".7" fill="currentColor" />
    <circle cx="15" cy="10" r=".7" fill="currentColor" />
    <path d="M5 9 4 7M19 9l1-2" />
  </svg>
);

export const ChickenIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M9 4c2 0 3 1.5 3 3 2 0 4 1.5 4 4v3c0 3-2 5-5 5H9c-3 0-5-2-5-5 0-3 2-5 4-5 0-3 1-5 1-5z" />
    <circle cx="11" cy="8" r=".6" fill="currentColor" />
    <path d="M9 4c0-1 .5-2 1-2M11 11l3 1" />
    <path d="M14 19l1 2M10 19l-1 2" />
  </svg>
);

export const TurkeyIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M12 4c4 0 7 3 7 7v3c0 3-3 5-7 5s-7-2-7-5v-3c0-4 3-7 7-7z" />
    <path d="M5 8c-1-1-2-1-2-1M19 8c1-1 2-1 2-1M3 13c-1 0-2 .5-2 1.5M21 13c1 0 2 .5 2 1.5" />
    <circle cx="12" cy="10" r=".6" fill="currentColor" />
  </svg>
);

export const LambIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <circle cx="12" cy="12" r="6" />
    <circle cx="9" cy="10" r="1.6" />
    <circle cx="15" cy="10" r="1.6" />
    <circle cx="12" cy="14" r="1.6" />
    <circle cx="8" cy="13" r="1.4" />
    <circle cx="16" cy="13" r="1.4" />
    <path d="M9 7c-1-1-2-1-3 0M15 7c1-1 2-1 3 0" />
  </svg>
);

export const BoarIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M4 13c0-4 3-7 8-7s8 3 8 7c0 3-3 5-8 5s-8-2-8-5z" />
    <path d="M5 11 3 9M19 11l2-2M9 18l-1 2M15 18l1 2" />
    <path d="M10 16l-2 2M14 16l2 2" />
    <circle cx="10" cy="11" r=".5" fill="currentColor" />
    <circle cx="14" cy="11" r=".5" fill="currentColor" />
  </svg>
);

/* Categories */
export const KnifeIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M3 17 17 3l4 4-4 4-10 10-4-4z" />
    <path d="m13 7 4 4" />
  </svg>
);

export const PotIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M4 9h16v7a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z" />
    <path d="M2 9h20M8 6V4M16 6V4M12 6V3" />
  </svg>
);

export const HandsKneadIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <ellipse cx="12" cy="14" rx="7" ry="3" />
    <path d="M5 14c-1 0-2 1-2 2M19 14c1 0 2 1 2 2" />
    <path d="M9 11c1-2 5-2 6 0" />
  </svg>
);

export const SausageIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M3 12c0-4 3-7 7-7h4c4 0 7 3 7 7s-3 7-7 7h-4c-4 0-7-3-7-7z" />
    <path d="M5 9.5c.5-.3 1-.3 1.5 0M9 8c.5-.4 1.2-.4 1.7 0M14 8c.5-.4 1.2-.4 1.7 0M18 9.5c.5-.3 1-.3 1.5 0" />
  </svg>
);

export const AbatinaSilhouetteIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M3 14c0-3 3-6 9-6s9 3 9 6c0 2-3 4-9 4s-9-2-9-4z" />
    <path d="M5 12c1-1 2-1 3 0M10 11c1-1 2-1 3 0M16 12c1-1 2-1 3 0" />
  </svg>
);

export const VacuumPackIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <rect x="4" y="5" width="16" height="14" rx="1.5" />
    <path d="M8 5v14M16 5v14M4 9h16" />
  </svg>
);

export const LeafIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M4 20c0-9 6-15 15-15-1 8-6 15-15 15z" />
    <path d="M4 20 14 10" />
  </svg>
);

export const MedalIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <circle cx="12" cy="14" r="6" />
    <path d="m8 4 4 4 4-4M9 4l3 5 3-5" />
  </svg>
);

export const HeartIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <path d="M12 20s-7-4.5-7-11a4 4 0 0 1 7-2 4 4 0 0 1 7 2c0 6.5-7 11-7 11z" />
  </svg>
);

export const EyeIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <ellipse cx="12" cy="12" rx="9" ry="5" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);

export const PeopleIcon = ({ size, className, ...p }: IconProps) => (
  <svg {...base(size, className)} {...p}>
    <circle cx="9" cy="9" r="3" />
    <circle cx="17" cy="10" r="2.5" />
    <path d="M3 19c0-3 3-5 6-5s6 2 6 5M14 19c0-2 2-4 5-4" />
  </svg>
);

import type { Animal, Category } from "@/data/products";

export function AnimalIcon({ animal, ...p }: { animal: Animal } & IconProps) {
  switch (animal) {
    case "manzo":
    case "vitello":
      return <CowIcon {...p} />;
    case "maiale":
      return <PigIcon {...p} />;
    case "pollo":
      return <ChickenIcon {...p} />;
    case "tacchino":
      return <TurkeyIcon {...p} />;
    case "agnello":
      return <LambIcon {...p} />;
    case "selvaggina":
      return <BoarIcon {...p} />;
    default:
      return <KnifeIcon {...p} />;
  }
}

export function CategoryIcon({ category, ...p }: { category: Category } & IconProps) {
  switch (category) {
    case "selezione-macellaio":
      return <KnifeIcon {...p} />;
    case "piatti-pronti":
      return <PotIcon {...p} />;
    case "preparati":
      return <HandsKneadIcon {...p} />;
    case "insaccati":
      return <SausageIcon {...p} />;
    case "abatina":
      return <AbatinaSilhouetteIcon {...p} />;
    case "preconfezionati":
      return <VacuumPackIcon {...p} />;
  }
}
